import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { MapPin, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { NEARBY, BRAND } from "../../data/content";
import { Reveal, SectionHeading } from "../shared/Primitives";
import { Sprig, DotGrid } from "../shared/Decor";
import { PlanLightbox } from "../PlanLightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const planUrl = (id) => `${BACKEND}/api/plans/photos/${id}`;

export const Location = () => {
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const scroller = useRef(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setPlans(r.data?.plans || [])).catch(() => {});
  }, []);

  const scrollBy = (dir) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: el.clientWidth * 0.85 * dir, behavior: "smooth" });
  };

  return (
    <>
      {/* Sección 1: Ubicación */}
      <section id="ubicacion" data-testid="location-section" className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-28">
        <Sprig className="absolute top-8 right-6 w-24 text-camel/25 hidden lg:block" />
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
          <SectionHeading
            overline="Dónde estamos"
            title="Ubicación"
            subtitle="En el sur de la Ciudad de México, con todo lo que necesitas a tu alrededor."
          />

          <div className="mt-12 grid lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
            {/* Info: dirección + puntos de interés */}
            <div className="space-y-10">
              {/* Dirección */}
              <Reveal>
                <div className="flex items-start gap-4">
                  <MapPin size={24} strokeWidth={1.6} className="text-cocoa mt-1 shrink-0" />
                  <div>
                    <span className="section-overline text-xs font-bold uppercase text-camel">Dirección</span>
                    <p data-testid="location-address" className="mt-3 text-lg font-light text-espresso/75 leading-relaxed">
                      {BRAND.address}
                      <br />
                      Col. Paraje 38, Tlalpan, Ciudad de México
                    </p>
                  </div>
                </div>
              </Reveal>

              {/* Puntos de interés */}
              <div>
                <Reveal>
                  <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-camel">
                    Puntos de interés cercanos
                  </h3>
                </Reveal>
                <ul className="mt-6 space-y-4">
                  {NEARBY.map((item, i) => (
                    <Reveal key={item} delay={0.05 + i * 0.05}>
                      <li data-testid={`nearby-${i}`} className="flex items-center gap-3">
                        <Check size={18} strokeWidth={2} className="text-cocoa shrink-0" />
                        <span className="text-lg font-light text-espresso/75">{item}</span>
                      </li>
                    </Reveal>
                  ))}
                </ul>
              </div>
            </div>

            {/* Mapa de ubicación */}
            <Reveal>
              <div className="flex flex-col gap-3 h-full">
                <div className="rounded-2xl overflow-hidden border border-khaki/60 flex-1 min-h-[360px]">
                  <iframe
                    data-testid="location-map"
                    title="Mapa de ubicación COLLECTIVE CO LIVING"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1882.9!2d-99.2075274!3d19.266176!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDE1JzU4LjIiTiA5OcKwMTInMjcuMSJX!5e0!3m2!1ses!2smx!4v1719200000000!5m2!1ses!2smx"
                    className="w-full h-full"
                    style={{ border: 0, minHeight: 360 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <a
                  data-testid="location-map-link"
                  href="https://www.google.com/maps/search/?api=1&query=19.266176,-99.2075274"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 self-start text-sm font-bold uppercase tracking-wide text-cocoa hover:text-espresso transition-colors"
                >
                  <MapPin size={16} strokeWidth={2} /> Abrir en Google Maps
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Sección 2: Planos del inmueble */}
      <section id="planos" data-testid="plans-section" className="relative overflow-hidden bg-linen py-16 sm:py-20 lg:py-28">
        <DotGrid className="absolute bottom-10 left-8 w-24 text-camel/25 hidden sm:block" />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
          <div className="text-center flex flex-col items-center">
            <SectionHeading
              overline="Distribución"
              title="Planos del inmueble"
              subtitle="Conoce la distribución de los espacios y cómo se organiza la comunidad."
            />
          </div>

          {plans.length > 0 ? (
            <div className="mt-12">
              {plans.length > 1 && (
                <div className="flex justify-end gap-3 mb-6">
                  <button onClick={() => scrollBy(-1)} data-testid="plans-prev" aria-label="Anterior" className="w-11 h-11 rounded-full border border-cocoa/40 text-cocoa flex items-center justify-center hover:bg-cocoa hover:text-linen transition-colors">
                    <ChevronLeft size={18} />
                  </button>
                  <button onClick={() => scrollBy(1)} data-testid="plans-next" aria-label="Siguiente" className="w-11 h-11 rounded-full border border-cocoa/40 text-cocoa flex items-center justify-center hover:bg-cocoa hover:text-linen transition-colors">
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
              <div ref={scroller} data-testid="plans-slider" className="flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2">
                {plans.map((p, i) => (
                  <figure key={p.id} data-testid={`plan-item-${i}`} className="snap-start shrink-0 w-[85%] sm:w-[60%] lg:w-[48%]">
                    <button
                      type="button"
                      onClick={() => setActivePlan(p)}
                      data-testid={`plan-open-${i}`}
                      aria-label={`Ampliar ${p.label}`}
                      className="group block w-full rounded-2xl overflow-hidden border border-khaki/60 bg-white cursor-zoom-in"
                    >
                      <img src={planUrl(p.id)} alt={p.label} className="w-full h-[320px] sm:h-[380px] object-contain bg-white transition-transform duration-300 group-hover:scale-[1.02]" />
                    </button>
                    <figcaption className="mt-3 text-base font-bold text-espresso text-center">{p.label}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          ) : (
            <Reveal className="mt-12">
              <div data-testid="plans-empty" className="mx-auto max-w-3xl rounded-2xl border border-dashed border-khaki/70 bg-white py-20 text-center">
                <p className="text-base font-light text-espresso/50">Los planos del inmueble estarán disponibles muy pronto.</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {activePlan && (
        <PlanLightbox src={planUrl(activePlan.id)} label={activePlan.label} onClose={() => setActivePlan(null)} />
      )}
    </>
  );
};
